import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { DeleteControlsMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

const mapTrpcResponseToGraphQL = (
  affectedRows: number
): DeleteControlsMutation => {
  return {
    deleteControlsById: {
      __typename: 'GenericMutationOutput',
      affected_rows: affectedRows,
    },
  };
};

export const useDeleteControlsTRPC = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { addNotification } = useNotifications();

  const mutation = useMutation({
    ...trpc.frontend.control.delete.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.control.register.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.control.controlById.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.control.controlsBasic.queryKey(),
        });
      },
    }),
    throwOnError: true,
  });

  useEffect(() => {
    if (trpcEnabled && mutation.error) {
      addNotification({
        type: 'error',
        content: mutation.error.message,
      });
    }
  }, [mutation.error, addNotification, trpcEnabled]);

  return {
    deleteControls: async (ids: string[]): Promise<DeleteControlsMutation> => {
      const result = await mutation.mutateAsync({ Ids: ids });

      return mapTrpcResponseToGraphQL(result.affected_rows);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
